import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FEATURE_FLAGS, flagEnabled, type AuthUser } from "../../../hooks/useAuth";
import { useTheme } from "../../../hooks/useTheme";
import { clearDashboardCache } from "../../../lib/dashboardCache";
import { adaptOwner } from "../../../pages/dashboard/dashboardAdapters";
import styles from "./ProfileMenu.module.css";
import { ProfileAvatar } from "./ProfileAvatar";

type ProfileMenuProps = {
  readonly user: AuthUser;
};

const getInitials = (firstName: string, lastName: string): string => {
  const first = firstName.charAt(0);
  const last = lastName.charAt(0);
  if (first.length === 0 && last.length === 0) return "?";
  return `${first}${last}`.toUpperCase();
};

const SunIcon = (): JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

const MoonIcon = (): JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
  </svg>
);

export const ProfileMenu = ({ user }: ProfileMenuProps): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const owner = adaptOwner(user);
  const initials = getInitials(owner.firstName, owner.lastName);
  const isDark = theme === "dark";

  const go = (path: string): void => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        data-testid="profile-trigger"
      >
        <ProfileAvatar url={user.avatarUrl} initials={initials} />
      </button>

      <div
        className={`${styles.panel} ${open ? styles.panelOpen : ""}`}
        role="menu"
        aria-label="Account menu"
      >
        <div className={styles.header}>
          <div className={styles.avatar} aria-hidden>
            <ProfileAvatar url={user.avatarUrl} initials={initials} />
          </div>
          <div className={styles.headerText}>
            <span className={styles.headerName}>
              {user.displayName?.trim() || `${owner.firstName} ${owner.lastName}`.trim()}
            </span>
            <span className={styles.headerEmail}>{owner.email}</span>
          </div>
        </div>

        {flagEnabled(user.features, FEATURE_FLAGS.userProfile) && (
          <>
            <div className={styles.divider} />
            <button type="button" role="menuitem" className={styles.item}
              onClick={() => go("/dashboard/profile")} data-testid="profile-edit">
              <span className={styles.itemMain}>Profile</span>
              <span className={styles.itemRight}>→</span>
            </button>
          </>
        )}

        {user.isAdmin ? (
          <>
            <div className={styles.divider} />
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => go("/dashboard/admin")}
            >
              <span className={styles.itemMain}>Admin</span>
              <span className={styles.itemRight}>→</span>
            </button>
          </>
        ) : null}

        {flagEnabled(user.features, FEATURE_FLAGS.billingCustomer) ? (
          <>
            <div className={styles.divider} />
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => go("/dashboard/billing")}
              data-testid="profile-billing"
            >
              <span className={styles.itemMain}>Billing</span>
              <span className={styles.itemRight}>→</span>
            </button>
          </>
        ) : null}

        <div className={styles.divider} />

        <button
          type="button"
          role="menuitem"
          className={styles.item}
          onClick={() => go("/dashboard/secure-notes")}
          data-testid="profile-secure-notes"
        >
          <span className={styles.itemMain}>Send secure note</span>
          <span className={styles.itemRight}>→</span>
        </button>

        <button
          type="button"
          role="menuitem"
          className={styles.item}
          onClick={() => go("/dashboard/site-logins")}
          data-testid="profile-site-logins"
        >
          <span className={styles.itemMain}>Website sign-ins</span>
          <span className={styles.itemRight}>→</span>
        </button>

        <div className={styles.divider} />

        <button
          type="button"
          role="menuitem"
          className={styles.item}
          onClick={toggle}
        >
          <span className={styles.itemMain}>
            <span className={styles.itemIcon}>{isDark ? <SunIcon /> : <MoonIcon />}</span>
            {isDark ? "Switch to light mode" : "Switch to dark mode"}
          </span>
        </button>

        <div className={styles.divider} />

        <a
          role="menuitem"
          href="/logout"
          className={`${styles.item} ${styles.itemDanger}`}
          onClick={() => clearDashboardCache()}
        >
          <span className={styles.itemMain}>Sign out</span>
        </a>
      </div>
    </div>
  );
};
